import { formatPrice } from '@utils/prices';
import { useTranslation } from 'next-i18next';

import { CurrencyCode } from '../../../types/Locations';
import { Frame } from '../../../types/Products';
import { getPreferenceLabelKeys } from '../../../utils/checkout/utils';
import { FramingSelections } from '../Framing/types';
import { getFramePrice } from '../Framing/utils/framePricing';
import { OrderReviewPreview } from './OrderReviewPreview';
import { CardHoldCard } from './ui/CardHoldCard';
import { DrawSummaryCard } from './ui/DrawSummaryCard';
import { OrderCalculation } from './ui/OrderCalculation';

export type OrderReviewItem = {
  id: string;
  title: string;
  image: string;
  price: number;
  quantity?: number;
  /** `false` hides every frame reference for a work that offers no framing. */
  hasFrameOptions?: boolean;
};

export type OrderReviewSummaryProps = {
  allowMultipleSelections?: boolean;
  bid?: string;
  cardHold?: { title: string; description: string };
  currencyCode: CurrencyCode;
  framingSelections: FramingSelections;
  freeShipping?: boolean;
  isEstimate: boolean;
  maxOrderValue: string;
  maxQuantity: number;
  maxQuantityNote?: {
    allocation?: string;
    label: string;
  };
  openSummaryIds: Record<string, boolean>;
  orderValue: string;
  orderedItems: OrderReviewItem[];
  shippingCost?: string;
  showPreference?: boolean;
  showPreview?: boolean;
  taxRate?: number;
  taxValue?: string;
  onAddFrame?: (itemId: string) => void;
  onToggleSummary: (itemId: string) => void;
};

export const OrderReviewSummary = ({
  allowMultipleSelections = false,
  bid,
  cardHold,
  currencyCode,
  framingSelections,
  freeShipping,
  isEstimate,
  maxOrderValue,
  maxQuantity,
  maxQuantityNote,
  onAddFrame,
  onToggleSummary,
  openSummaryIds,
  orderValue,
  orderedItems,
  shippingCost,
  showPreference = true,
  showPreview = false,
  taxRate,
  taxValue,
}: OrderReviewSummaryProps) => {
  const { t } = useTranslation('checkout');

  if (orderedItems.length === 0) {
    return null;
  }

  let maxOrderValueLabel: string | undefined;
  if (allowMultipleSelections) {
    maxOrderValueLabel = t('orderCalculation.totalEstimated');
  }

  const taxText = !taxValue
    ? undefined
    : taxRate === undefined
      ? t('orderCalculation.tax.label')
      : t('orderCalculation.tax.withRate', { rate: taxRate });

  return (
    <div className="flex w-full flex-col gap-3">
      <h3 className="font-bold lg:hidden">{t('orderReview.summary')}</h3>
      {showPreview && (
        <OrderReviewPreview
          images={orderedItems.map((item) => ({
            id: item.id,
            src: item.image,
            alt: item.title,
          }))}
        />
      )}

      {orderedItems.map((item, index) => {
        const rank = index + 1;
        const isBackup = rank > maxQuantity;
        const selection = framingSelections[item.id];
        const frame: Frame | undefined =
          selection === undefined || selection === null ? undefined : selection;
        const framePrice = frame ? getFramePrice(frame) : undefined;
        const { choiceKey, ordinalKey } = getPreferenceLabelKeys(
          rank,
          isBackup,
        );

        const choiceState = isBackup ? 'text-active-4' : 'text-positive-4';
        const preferenceText = showPreference
          ? t(choiceKey, { rank: t(ordinalKey, { rank }) })
          : undefined;
        const subText = allowMultipleSelections
          ? t('orderReview.quantityLabel', { quantity: item.quantity ?? 1 })
          : preferenceText;
        const subTextState = allowMultipleSelections
          ? 'text-neutral-4'
          : choiceState;

        return (
          <DrawSummaryCard
            key={item.id}
            className="m-0"
            frameOption={frame?.title}
            hasFrameOptions={item.hasFrameOptions ?? true}
            framePrice={
              framePrice
                ? String(formatPrice(framePrice, currencyCode))
                : undefined
            }
            image={item.image}
            isOpen={Boolean(openSummaryIds[item.id])}
            isUnframed={selection === null}
            onAddFrame={
              onAddFrame && selection === null
                ? () => onAddFrame(item.id)
                : undefined
            }
            onToggle={() => onToggleSummary(item.id)}
            productPrice={String(formatPrice(item.price, currencyCode))}
            subText={subText}
            subTextState={subTextState}
            title={item.title}
            totalPrice={String(
              formatPrice(item.price + (framePrice ?? 0), currencyCode),
            )}
          />
        );
      })}

      {maxQuantityNote && (
        <div className="flex flex-col gap-3 p-4">
          <div className="flex flex-row justify-between gap-3">
            <span>{maxQuantityNote.label}</span>
            <span>{maxQuantity}</span>
          </div>
          {maxQuantityNote.allocation && (
            <p className="text-neutral-4">{maxQuantityNote.allocation}</p>
          )}
        </div>
      )}

      <OrderCalculation
        bid={bid}
        freeShipping={freeShipping}
        isEstimate={isEstimate}
        maxOrderValue={maxOrderValue}
        maxOrderValueLabel={maxOrderValueLabel}
        orderValue={orderValue}
        shippingCost={shippingCost}
        taxText={taxText}
        taxValue={taxValue}
      />

      {cardHold && (
        <CardHoldCard
          amount={maxOrderValue}
          description={cardHold.description}
          title={cardHold.title}
        />
      )}
    </div>
  );
};
